import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Custom hook for making API calls with loading and error states
 * @returns {Object} - { loading, error, request }
 */
export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const request = useCallback(async (method, endpoint, data = null, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const config = {
        method,
        url: `${API}${endpoint}`,
        ...options,
      };
      
      if (data) {
        config.data = data;
      }
      
      const response = await axios(config);
      return { data: response.data, error: null };
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'An error occurred';
      setError(errorMessage);
      if (!options.silent) {
        toast.error(errorMessage);
      }
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  const get = useCallback((endpoint, options) => request('GET', endpoint, null, options), [request]);
  const post = useCallback((endpoint, data, options) => request('POST', endpoint, data, options), [request]);
  const put = useCallback((endpoint, data, options) => request('PUT', endpoint, data, options), [request]);
  const del = useCallback((endpoint, options) => request('DELETE', endpoint, null, options), [request]);

  return { loading, error, request, get, post, put, del };
};

export default useApi;
